import { useTranslation } from "react-i18next";

import {
  ApplicationSubtype,
  AsyncCallArgs,
  BoxSpg,
  CardSpg,
  CircularTextLoader,
  MediumButton,
  useAsyncCall,
} from "@duosoftbg/nacid-components";
import { Alert, CardContent, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PageWrapper from "../../../common/layout/PageWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faMoneyBills } from "@fortawesome/free-solid-svg-icons";
import { createMyApplicationsUrl, getApplicationBaseUrl } from "../../../../../utils/applicationUrlUtils";
import { fetchSignStatus } from "../../../../../services/appSignCalls";
import { calculateFeesForId } from "../../../../../services/serviceCalls";

const SignedDocumentResultPage = () => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const fileId = searchParams.get("fileId");
  const id = searchParams.get("id");
  const appSubType = searchParams.get("app_sub_Type");
  const isDoc_SE = appSubType.includes("SE_");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState(null);
  const [calculatedFees, setCalculatedFees] = useState(null);

  const getSignStatus = () => {
    const fileApplicationAsyncCall: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: fetchSignStatus(id, fileId),
      processResponseErrors: false,
      onSuccess: (response) => {
        setStatus(response.data);
        setError(false);
        setLoading(false);
      },
      onError: () => {
        setStatus(null);
        setError(true);
        setLoading(false);
      },
    };
    asyncCall(fileApplicationAsyncCall);
  };

  const getFees = () => {
    const getFeesAsyncCall: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: calculateFeesForId(getApplicationBaseUrl(appSubType as ApplicationSubtype), id),
      processResponseErrors: false,
      onSuccess: (response) => {
        setCalculatedFees(response.data);
      },
      onError: () => {
        setCalculatedFees(null);
      },
    };
    asyncCall(getFeesAsyncCall);
  };

  useEffect(() => {
    getSignStatus();
    getFees();
  }, [fileId, id, appSubType]);

  if (loading) {
    return (
      <PageWrapper title={t("t.page.signed.document.details")}>
        <CardSpg my={4} style={{ overflow: "visible" }}>
          <CardContent style={{ position: "relative" }}>
            <BoxSpg>
              <CircularTextLoader />
            </BoxSpg>
          </CardContent>
        </CardSpg>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper title={t("t.page.signed.document.details")}>
        <CardSpg my={4} style={{ overflow: "visible" }}>
          <CardContent style={{ position: "relative" }}>
            <BoxSpg>
              <Alert severity={"error"}>{t("m.generic.error.service.fail")}</Alert>
            </BoxSpg>
          </CardContent>
        </CardSpg>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title={t("t.page.signed.document.details")}>
      <CardSpg my={4} style={{ overflow: "visible" }}>
        <CardContent style={{ position: "relative" }}>
          <BoxSpg>
            {status.customerSigned && <Alert severity={"success"}>{t("m.document.signed")}</Alert>}
            {status.customerSigned && (
              <Alert severity="success">
                {status.entryNumbers.length === 1
                  ? t("m.document.signed.single", { entryNumber: status.entryNumbers[0] })
                  : t("m.document.signed.multiple", { entryNumbers: status.entryNumbers.join(", ") })}
              </Alert>
            )}
            {status.customerSigned && (
              <Alert severity={"warning"}>{isDoc_SE ? t("m.required.payment.se") : t("m.required.payment")}</Alert>
            )}
            {!status.customerSigned && <Alert severity={"error"}>{t("m.document.not.sign")}</Alert>}
          </BoxSpg>
          <BoxSpg mt={4}>
            <Typography align={"right"}>
              {status.customerSigned && (calculatedFees?.fees?.length || 0) > 0 && (
                <MediumButton
                  ml={2}
                  startIcon={<FontAwesomeIcon style={{ fontSize: 12 }} icon={faMoneyBills} />}
                  variant="outlined"
                  color="primary"
                  onClick={() => window.location.replace(`${process.env.REACT_APP_PAYMENTS_URL}/user-payments`)}
                >
                  {t("l.btn.pay.online")}
                </MediumButton>
              )}

              <MediumButton
                ml={2}
                startIcon={<FontAwesomeIcon style={{ fontSize: 12 }} icon={faList} />}
                variant="outlined"
                color="primary"
                onClick={() => navigate(createMyApplicationsUrl())}
              >
                {t("l.btn.myApplications")}
              </MediumButton>
            </Typography>
          </BoxSpg>
        </CardContent>
      </CardSpg>
    </PageWrapper>
  );
};
export default SignedDocumentResultPage;
